import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {BugsnagSeveritiesEnum} from '../../../libs/shared/helpers/src/bugsnag/bugsnagSeverities';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {ResponseInfo} from './legacyPerformanceMiddleware';
import {FetchInfos, fetchInfosFromResponse} from './performanceMetricHelpers';

/*
 * Types.
 */

export interface SwitchFolderEvent {
  /** Approximate time of the event. */
  timestamp: number;
  /** Path of the folder. */
  path: string;
  /** Total clock time from location change to P2 paint complete. */
  totalTime: DOMHighResTimeStamp;
  /** Total clock time from fetch start to fetch complete, either from network or cache. */
  totalFetchTime: DOMHighResTimeStamp;
  /** Fetch time we actually spent waiting for the fetch, and were not busy doing other things. */
  idleFetchTime: DOMHighResTimeStamp;
  /** Time we were busy, mostly rendering and painting (totalTime - idleFetchTime). */
  busyTime: DOMHighResTimeStamp;
  /** Number of cells in P2. */
  cellCount: number;
  /** Additional infos about how the fetching occured. */
  fetchInfos: FetchInfos | undefined;
}

export interface SwitchFolderMetricOptions {
  shouldCreatePerformanceEntries?: boolean;
  shouldLogEventsAsTable?: boolean;
  onNewEvent: HandlerOf<SwitchFolderEvent>;
}

/*
 * Constants.
 */

const maxExpectedTimeBeforeFetch = 10 * 1000;
const maxEventCount = 100;

const measureName = '💌 Switch folder';
const idleFetchMeasureName = '💌 Fetching cells';

/*
 * Metric.
 */

export class SwitchFolderMetric {
  private readonly shouldCreatePerformanceEntries: boolean;
  private readonly shouldLogEventsAsTable: boolean;
  private readonly onNewEvent: HandlerOf<SwitchFolderEvent>;
  private readonly events: SwitchFolderEvent[] = [];
  private readonly timeouts: number[] = [];

  private path?: string;
  private startTimeStamp: DOMHighResTimeStamp = 0;
  private fetchStartTimeStamp: DOMHighResTimeStamp = 0;
  private fetchFirstIdleTimeStamp: DOMHighResTimeStamp = 0;
  private fetchEndTimeStamp: DOMHighResTimeStamp = 0;
  private endTimeStamp: DOMHighResTimeStamp = 0;

  constructor(options: SwitchFolderMetricOptions) {
    this.shouldCreatePerformanceEntries = options.shouldCreatePerformanceEntries !== false;
    this.shouldLogEventsAsTable = Boolean(options.shouldLogEventsAsTable);
    this.onNewEvent = options.onNewEvent;
  }

  private reset() {
    this.path = undefined;
    this.startTimeStamp = 0;
    this.endTimeStamp = 0;

    while (this.timeouts.length) {
      globalWindow.clearTimeout(this.timeouts.pop());
    }

    this.resetFetch();
  }

  private resetFetch() {
    this.fetchStartTimeStamp = 0;
    this.fetchFirstIdleTimeStamp = 0;
    this.fetchEndTimeStamp = 0;
  }

  markStart(path: string) {
    this.reset();
    this.path = path;
    this.startTimeStamp = performance.now();

    // The fetch should start after one macrotask, so if it didn't happen after two macrotasks then it
    // probably wasn't a normal UI interaction.
    this.startTimeout(() => {
      this.startTimeout(() => {
        if (!this.fetchStartTimeStamp) {
          this.reset();
        }
      });
    });
  }

  markFetchStart() {
    const {startTimeStamp} = this;
    if (!startTimeStamp) {
      return;
    }

    this.fetchStartTimeStamp = performance.now();

    // Measure the time we're busy doing other stuff after we start the fetch and before it completes.
    this.startTimeout(() => {
      this.fetchFirstIdleTimeStamp = performance.now();
    });
  }

  markFetchEnd() {
    // Use the first fetch completion timestamp, which may be from a cache hit!
    if (this.fetchEndTimeStamp) {
      return;
    }

    this.fetchEndTimeStamp = performance.now();

    try {
      if (this.shouldCreatePerformanceEntries) {
        performance.measure(idleFetchMeasureName, {
          start: this.fetchFirstIdleTimeStamp,
          end: this.fetchEndTimeStamp,
        });
      }
    } catch {
      // Ignore.
    }
  }

  markEndAfterCurrentTask(cellCount: number, responseInfo: ResponseInfo | undefined) {
    this.startTimeout(() => {
      this.markEnd(cellCount, responseInfo);
    });
  }

  markEnd(cellCount: number, responseInfo: ResponseInfo | undefined) {
    this.endTimeStamp = performance.now();

    try {
      if (this.shouldCreatePerformanceEntries) {
        performance.measure(measureName, {
          start: this.startTimeStamp,
          end: this.endTimeStamp,
        });
      }
    } catch {
      // Ignore.
    }

    try {
      this.maybePushEvent(cellCount, responseInfo);

      if (this.shouldLogEventsAsTable) {
        // eslint-disable-next-line no-console
        console.table([...this.events], ['timestamp', 'path', 'busyTime', 'cellCount']);
      }
    } finally {
      // Don't keep around an old startTimeStamp!
      this.reset();
    }
  }

  private startTimeout(callback: Handler) {
    const id = globalWindow.setInstrumentedTimeout(callback, 0);
    this.timeouts.push(id);
  }

  private maybePushEvent(cellCount: number, responseInfo: ResponseInfo | undefined) {
    const {path} = this;
    if (!path) {
      return;
    }

    const {startTimeStamp, endTimeStamp} = this;
    const totalTime =
      startTimeStamp && endTimeStamp && startTimeStamp < endTimeStamp ? endTimeStamp - startTimeStamp : 0;
    if (!totalTime) {
      return;
    }

    const {fetchStartTimeStamp, fetchFirstIdleTimeStamp, fetchEndTimeStamp} = this;
    if (!fetchStartTimeStamp) {
      return;
    }

    // Sanity check to make sure the startTimeStamp and fetchStartTimeStamp are reasonably related.
    const timeBeforeFetch = fetchStartTimeStamp - startTimeStamp;
    if (timeBeforeFetch > maxExpectedTimeBeforeFetch) {
      const error = new Error(
        `Start time and fetch time mismatch in folder switch metric (${timeBeforeFetch}ms)`,
      );
      reportToBugsnag(error, {
        severity: BugsnagSeveritiesEnum.INFO,
      });
    }

    const totalFetchTime =
      fetchStartTimeStamp && fetchEndTimeStamp && fetchStartTimeStamp < fetchEndTimeStamp
        ? fetchEndTimeStamp - fetchStartTimeStamp
        : 0;
    const busyFetchTime =
      fetchStartTimeStamp && fetchFirstIdleTimeStamp && fetchStartTimeStamp < fetchFirstIdleTimeStamp
        ? Math.min(fetchFirstIdleTimeStamp - fetchStartTimeStamp, totalFetchTime)
        : 0;
    const idleFetchTime = totalFetchTime - busyFetchTime;
    const busyTime = totalTime - idleFetchTime;

    const fetchInfos = responseInfo && fetchInfosFromResponse(responseInfo);

    const event = {
      timestamp: performance.timeOrigin + startTimeStamp,
      path,
      totalTime,
      totalFetchTime,
      idleFetchTime,
      busyTime,
      cellCount,
      fetchInfos,
    };

    this.events.push(event);

    this.onNewEvent(event);

    if (this.events.length > maxEventCount) {
      this.events.splice(0, this.events.length - maxEventCount);
    }
  }
}
